import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Switch, Route } from 'react-router-dom';
import { OrgFeatures } from '@bb/domain/dist/types';
import { AuthContext } from 'contexts';
import Axios from 'axios';
import PartialBanner from './PartialBanner';
import NotAvailableBanner from './NotAvailableBanner';

type RouteData = Partial<Record<OrgFeatures, string[]>>;
type RouteDataEntries = [OrgFeatures, string[]];

const NOT_AVAILABLE_ROUTES: RouteData = {
  ANALYTICS: ['/analytics/market', '/analytics/dashboard'],
  ASSETS: ['/assets'],
  CHAT: ['/inbox'],
  CREATE_LISTING: [
    '/listing/new',
    '/listing/list/user',
    '/listing/:listingId/documents',
  ],
  MY_BROKERAGE: [
    '/admin',
    '/admin/dashboard',
    '/announcement/list',
    '/announcement/new',
    '/listing/list/admin/:page',
    '/listing/list/brokerage/:page',
    '/listing/list/deleted/:page',
    '/listing/new',
    '/needs/:page',
    '/open_houses',
    '/users/edit/:userId',
    '/users/list',
  ],
  OFFERS: ['/listing/:listingId/offer_settings'],
  TEAMS: ['/team_settings', '/listing/list/team', '/appointment/list/team'],
  VIRTUAL_SHOWINGS: ['/listing/:listingId/tour/:tourId/details'],
  SHOWING_INSIGHTS: ['/analytics/insight'],
};

const PARTIAL_ROUTES: RouteData = {
  ANALYTICS: ['/'],
  ASSETS: ['/'],
  CHAT: ['/'],
  COVID_19: [
    '/org/settings',
    '/listing/:listingId/appointments/config',
    '/listing/:listingId/showings',
    '/appointment/list/team',
    '/appointments/:appointmentId/action',
    '/admin/dashboard',
    '/my_business/showings/buyer',
  ],
  CREATE_LISTING: ['/'],
  MY_BROKERAGE: ['/'],
  OFFERS: ['/'],
  TEAMS: ['/'],
  VIRTUAL_SHOWINGS: [
    '/listing/:listingId/appointments/book',
    '/listing/:listingId/showings',
    '/my_business',
  ],
};

const FeatureBanner: React.FC = () => {
  const { auth } = useContext(AuthContext);

  const [currentPlan, setCurrentPlan] = useState(undefined);

  const features = useMemo(
    () => new Set<OrgFeatures>(auth.org.features),
    [auth?.org.features],
  );

  useEffect(() => {
    const getCurrentPlan = async () => {
      const { data } = await Axios.get(`/org/plan`);
      setCurrentPlan(data?.org?.orgCustomerSuccess?.currentPlan);
    };

    getCurrentPlan();
  }, [auth?.org.id]);

  const getPaths = (routeData: RouteData) =>
    Object.entries(routeData).reduce(
      (acc, [feature, paths]: RouteDataEntries) => {
        if (features.has(feature)) return acc;
        return [...acc, ...paths];
      },
      [],
    );

  const notAvailablePaths = getPaths(NOT_AVAILABLE_ROUTES);
  const partialPaths = getPaths(PARTIAL_ROUTES);

  if (
    currentPlan?.name !== 'AGENT' ||
    !(auth.assumingUserId || auth.isAssumedNotBB)
  ) {
    return null;
  }

  return (
    <Switch>
      <Route path={notAvailablePaths}>
        <NotAvailableBanner />
      </Route>
      <Route path={partialPaths}>
        <PartialBanner />
      </Route>
    </Switch>
  );
};

export default FeatureBanner;
